import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { InputNumber } from "primereact/inputnumber";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";
import { getMinAppVersion, setMinAppVersion } from "../../services/appVersion";

const formatVersion = (
  year: number | null,
  week: number | null,
  patch: number | null
): string | null => {
  if (!year || !week || patch === null || patch === undefined) return null;
  return `${year}.${String(week).padStart(2, "0")}.${patch}`;
};

const AppVersionPage = () => {
  const toast = useRef<Toast>(null);
  const [year, setYear] = useState<number | null>(null);
  const [week, setWeek] = useState<number | null>(null);
  const [patch, setPatch] = useState<number | null>(null);
  const [currentVersion, setCurrentVersion] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingInitial, setIsFetchingInitial] = useState(true);

  const fetchMinAppVersion = useCallback(async () => {
    try {
      const data: { min_version: string | null } = await getMinAppVersion();
      if (!data.min_version) return;
      const parts = data.min_version.split(".");
      if (
        parts.length !== 3 ||
        parts.some((p) => !Number.isFinite(Number(p)))
      ) {
        throw new Error(`Invalid version format: ${data.min_version}`);
      }
      setYear(Number(parts[0]));
      setWeek(Number(parts[1]));
      setPatch(Number(parts[2]));
      setCurrentVersion(data.min_version);
    } catch {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to load the current minimum app version.",
        life: 3000,
      });
    } finally {
      setIsFetchingInitial(false);
    }
  }, []);

  useEffect(() => {
    fetchMinAppVersion();
  }, [fetchMinAppVersion]);

  const handleSave = async () => {
    if (!year || !week || patch === null || patch === undefined) {
      toast.current?.show({
        severity: "error",
        summary: "Validation Error",
        detail: "Please fill in all fields.",
        life: 3000,
      });
      return;
    }

    const version = formatVersion(year, week, patch)!;

    setIsLoading(true);
    try {
      await setMinAppVersion(version);
      setCurrentVersion(version);
      toast.current?.show({
        severity: "success",
        summary: "Saved",
        detail: `Minimum app version updated to ${version}`,
        life: 3000,
      });
    } catch {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to update the minimum app version.",
        life: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const preview = formatVersion(year, week, patch);

  if (isFetchingInitial) {
    return (
      <div
        className="flex justify-content-center align-items-center"
        style={{ minHeight: "300px" }}
      >
        <ProgressSpinner />
      </div>
    );
  }

  return (
    <div className="p-4">
      <Toast ref={toast} />
      <Card title="Mobile App Version Requirement">
        <p className="mt-0 mb-4 text-color-secondary">
          Set the minimum version of the mobile app required to use the service.
          Format: <strong>YYYY.WW.PP</strong> (Year · Calendar Week · Patch)
        </p>

        {currentVersion && (
          <div className="flex align-items-center gap-2 mb-4">
            <span className="text-color-secondary">
              Current minimum version:
            </span>
            <span
              className="px-3 py-1 border-round-xl font-semibold text-sm"
              style={{
                background: "var(--green-100)",
                color: "var(--green-700)",
              }}
            >
              {currentVersion}
            </span>
          </div>
        )}

        <div className="mt-6 mb-4 font-bold text-xl">New minimum version</div>
        <div
          className="flex align-items-end justify-content-between mb-3"
          style={{ width: "40rem" }}
        >
          <div className="field mb-0">
            <label className="block mb-1 text-sm text-color-secondary">
              Year
            </label>
            <InputNumber
              value={year}
              onValueChange={(e) => setYear(e.value ?? null)}
              useGrouping={false}
              min={2020}
              max={2099}
              placeholder="YYYY"
              style={{ width: "5rem" }}
            />
          </div>

          <div className="field mb-0">
            <label className="block mb-1 text-sm text-color-secondary">
              Week
            </label>
            <InputNumber
              value={week}
              onValueChange={(e) => setWeek(e.value ?? null)}
              useGrouping={false}
              min={1}
              max={53}
              placeholder="WW"
              style={{ width: "5rem" }}
            />
          </div>

          <div className="field mb-0">
            <label className="block mb-1 text-sm text-color-secondary">
              Patch
            </label>
            <InputNumber
              value={patch}
              onValueChange={(e) => setPatch(e.value ?? null)}
              useGrouping={false}
              min={0}
              placeholder="PP"
              style={{ width: "5rem" }}
            />
          </div>
        </div>

        {preview && (
          <p className="italic mt-2 mb-4 text-color-secondary text-xl">
            Version Preview: <strong>{preview}</strong>
          </p>
        )}

        <div className="flex justify-content-end">
          <Button
            label="Save"
            icon="pi pi-save"
            loading={isLoading}
            onClick={handleSave}
          />
        </div>
      </Card>
    </div>
  );
};

export default AppVersionPage;
